import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  type SettingKey,
  type SettingStatus,
  getSettings,
  logout,
  setSetting,
  testSetting,
} from "../lib/api";
import { Button } from "../components/Form";
import { ThemeToggle } from "../components/ThemeToggle";
import { useToast } from "../components/Toast";

/**
 * Map provider/model setting values → human-friendly toast labels.
 * fix38: When the user picks a different provider or model, we surface
 * a confirmation toast like "AI detection set to Winston AI". Adding new
 * options to a select also requires adding a label here, otherwise the
 * toast falls through to the raw value (e.g. "ai_detection_provider set
 * to my_new_provider"). Keys not in this map don't toast at all — only
 * the three provider/model selects + a few content-engine config keys.
 */
const TOAST_LABELS: Partial<Record<SettingKey, { name: string; values: Record<string, string> }>> = {
  image_generation_provider: {
    name: "Image generation provider",
    values: {
      openai: "OpenAI (DALL-E)",
      google_gemini: "Google Gemini",
    },
  },
  ai_detection_provider: {
    name: "AI detection",
    values: {
      winston: "Winston AI",
      gptzero: "GPTZero",
      zerogpt: "ZeroGPT",
      none: "Disabled",
    },
  },
  humanize_model: {
    name: "Humanize model",
    values: {
      stealthgpt: "StealthGPT",
      gemini_flash: "Gemini 2.5 Flash",
      openai_gpt5_mini: "OpenAI GPT-5.4 Mini",
      claude_sonnet: "Claude Sonnet",
      none: "Disabled",
    },
  },
};

/**
 * Settings page — global configuration.
 *
 * Sections:
 *   1. Account (logout link)
 *   2. Image Generation (provider dropdown)
 *   3. AI Detection (provider dropdown)
 *   4. Humanize Model (model dropdown)
 *   5. API Keys (set/update/test grid)
 *   6. YouTube / Google OAuth (set fields)
 *
 * API keys are encrypted at rest. UI never shows plaintext.
 */
export default function SettingsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [settings, setSettings] = useState<SettingStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const data = await getSettings();
      setSettings(data.settings);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load settings.");
    } finally {
      setLoading(false);
    }
  }

  /**
   * Soft refresh — used after Save/Test/Clear actions. Updates `settings`
   * without flipping the `loading` flag, so the page doesn't unmount the
   * form into the "Loading…" placeholder (which causes scroll loss to top).
   */
  async function softRefresh() {
    try {
      const data = await getSettings();
      setSettings(data.settings);
    } catch (err) {
      // Don't clobber the page on a transient refresh failure; keep the old
      // settings visible. The next action will retry.
      console.error("Settings refresh failed:", err);
    }
  }

  useEffect(() => {
    refresh().catch(console.error);
  }, []);

  function getValue(key: SettingKey): SettingStatus | undefined {
    return settings.find((s) => s.key === key);
  }

  async function handleSetValue(key: SettingKey, value: string | null) {
    try {
      await setSetting(key, value);
      await softRefresh();
      // chunk3z-fix38: emit a confirmation toast for provider/model changes.
      // Only for keys present in TOAST_LABELS — saving an API key (where
      // value is the secret itself) doesn't need a toast and would leak
      // the key into the toast message. Clearing a value (value=null)
      // also skips the toast since "AI detection set to null" is gibberish.
      const labelInfo = TOAST_LABELS[key];
      if (labelInfo && value) {
        const friendlyValue = labelInfo.values[value] ?? value;
        // fix40: 2-second duration. The default 10s is overkill for a
        // simple "X set to Y" confirmation — the user just made the change,
        // they don't need to study the toast.
        toast.show(`${labelInfo.name} set to ${friendlyValue}`, {
          kind: "success",
          durationMs: 2000,
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed";
      console.error(`[Settings] setSetting(${key}) failed:`, err);
      alert(`Could not save ${key}: ${msg}`);
      throw err;
    }
  }

  async function handleTest(key: SettingKey) {
    try {
      await testSetting(key);
      await softRefresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Test failed";
      console.error(`[Settings] testSetting(${key}) failed:`, err);
      alert(`Could not test ${key}: ${msg}`);
      throw err;
    }
  }

  async function handleLogout() {
    try {
      await logout();
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      navigate("/login", { replace: true });
    }
  }

  return (
    <div className="min-h-screen bg-bg-subtle">
      <header className="bg-bg-card border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-text-muted hover:text-text text-sm">
              ← Back
            </Link>
            <h1 className="text-text font-semibold">Settings</h1>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button
              onClick={handleLogout}
              className="bg-danger-bg hover:bg-danger/10 text-danger border border-danger-border text-sm font-medium rounded-md px-3 py-1.5 transition"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-4">
        {loading && (
          <div className="bg-bg-card border border-border rounded-lg p-6 shadow-card">
            <p className="text-text-muted text-sm">Loading settings...</p>
          </div>
        )}

        {error && (
          <div className="bg-danger-bg border border-danger-border rounded-lg p-4">
            <p className="text-danger text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            <AccountCard onLogout={handleLogout} />

            <ImageGenerationCard
              currentValue={getValue("image_generation_provider")?.value ?? "openai"}
              onSet={(v) => handleSetValue("image_generation_provider", v)}
            />

            <AiDetectionCard
              currentValue={getValue("ai_detection_provider")?.value ?? "winston"}
              onSet={(v) => handleSetValue("ai_detection_provider", v)}
            />

            <HumanizeModelCard
              currentValue={getValue("humanize_model")?.value ?? "stealthgpt"}
              onSet={(v) => handleSetValue("humanize_model", v)}
            />

            <ApiKeysCard
              settings={settings}
              onSet={handleSetValue}
              onTest={handleTest}
            />

            <YouTubeOAuthCard
              clientId={getValue("google_oauth_client_id")}
              clientSecret={getValue("google_oauth_client_secret")}
              onSet={handleSetValue}
            />

            {/* chunk3z-fix424: Search Console uses a SEPARATE OAuth client from
                YouTube — different project, different scopes. Sharing one would
                break YouTube uploads as soon as GSC is connected. */}
            <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
              <h2 className="text-text font-semibold mb-1">
                Google Search Console
              </h2>
              <p className="text-text-muted text-sm mb-3">
                Used by the Sitemaps tab to cross-check the archive scan — GSC
                knows which URLs Google indexed, including pages Wayback never
                captured. Create a separate OAuth client (Web application) with
                the redirect URI{" "}
                <code className="text-xs bg-bg-subtle px-1 rounded">
                  {window.location.origin}/api/gsc/callback
                </code>
                .
              </p>
              <div className="space-y-3">
                <SimpleSecretRow
                  label="Search Console Client ID"
                  status={getValue("gsc_client_id")}
                  settingKey="gsc_client_id"
                  onSet={handleSetValue}
                  link={{
                    href: "https://console.cloud.google.com/auth/clients",
                    text: "Open Console",
                  }}
                  notes="From the OAuth client you created for Search Console. Scope needed: webmasters.readonly."
                />
                <SimpleSecretRow
                  label="Search Console Client Secret"
                  status={getValue("gsc_client_secret")}
                  settingKey="gsc_client_secret"
                  onSet={handleSetValue}
                  notes="Pair with the Client ID above. Connect the account in the Sitemaps tab."
                />
                {/* chunk3z-fix426: Google matches this string exactly — www and
                    bare host are different URIs. Setting it explicitly avoids
                    redirect_uri_mismatch when the app is reached either way. */}
                <SimpleSecretRow
                  label="Redirect URI"
                  status={getValue("gsc_redirect_uri")}
                  settingKey="gsc_redirect_uri"
                  onSet={handleSetValue}
                  notes={`Must match Google Cloud EXACTLY, including www. This app is currently at ${window.location.origin}, so use ${window.location.origin}/api/gsc/callback`}
                />
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

// =========================================================================
// Account
// =========================================================================
function AccountCard({ onLogout }: { onLogout: () => void }) {
  return (
    <Card>
      <h2 className="text-text font-semibold mb-1">Account</h2>
      <p className="text-text-muted text-sm">Internal team workspace</p>
      <p className="text-text-subtle text-xs mt-2">
        API keys are encrypted and stored securely on the server. They are never exposed in your browser.
      </p>
      <div className="mt-4">
        <Button variant="secondary" onClick={onLogout}>
          Sign Out
        </Button>
      </div>
    </Card>
  );
}

// =========================================================================
// Image Generation
// =========================================================================
function ImageGenerationCard({
  currentValue,
  onSet,
}: {
  currentValue: string;
  onSet: (v: string | null) => Promise<void>;
}) {
  const options = [
    { value: "openai", label: "OpenAI (DALL-E)" },
    { value: "google_gemini", label: "Google Gemini" },
  ];
  return (
    <Card>
      <h2 className="text-text font-semibold mb-1">Image Generation</h2>
      <SettingsSelect
        label="Provider"
        value={currentValue}
        options={options}
        onChange={(v) => onSet(v)}
      />
      <p className="text-text-subtle text-xs mt-2">
        Controls which API is used for image generation. This is the same default that appears in <span className="text-text">Content Engine</span> on the Build Queue tab — change it in either place. Both API keys can be stored — switch freely.
      </p>
    </Card>
  );
}

// =========================================================================
// AI Detection
// =========================================================================
function AiDetectionCard({
  currentValue,
  onSet,
}: {
  currentValue: string;
  onSet: (v: string | null) => Promise<void>;
}) {
  const options = [
    { value: "winston", label: "Winston AI" },
    { value: "gptzero", label: "GPTZero" },
    { value: "zerogpt", label: "ZeroGPT" },
    { value: "none", label: "None (Disable)" },
  ];
  return (
    <Card>
      <h2 className="text-text font-semibold mb-1">AI Detection</h2>
      <SettingsSelect
        label="Provider"
        value={currentValue}
        options={options}
        onChange={(v) => onSet(v)}
      />
      <p className="text-text-muted text-sm mt-3">
        Scores content for AI detection probability during generation. Mirrored in <span className="text-text">Content Engine</span> on the Build Queue tab — change it in either place.
      </p>
      <p className="text-text-subtle text-xs mt-2">
        In our experience, AI detection scores don't significantly impact search rankings — the bigger value is catching content that still reads robotic after humanization. ZeroGPT is inexpensive and sufficient for that check. Winston AI and GPTZero produce stricter scores, but the benefits over ZeroGPT are marginal at best unless you have a specific reason (e.g., a client who explicitly audits for AI content with those tools). If you need one, Winston is available at gowinston.ai.
      </p>
    </Card>
  );
}

// =========================================================================
// Humanize Model
// =========================================================================
function HumanizeModelCard({
  currentValue,
  onSet,
}: {
  currentValue: string;
  onSet: (v: string | null) => Promise<void>;
}) {
  const options = [
    { value: "stealthgpt", label: "StealthGPT" },
    { value: "gemini_flash", label: "Gemini 2.5 Flash" },
    { value: "openai_gpt5_mini", label: "OpenAI GPT-5.4 Mini" },
    { value: "claude_sonnet", label: "Claude Sonnet" },
    { value: "none", label: "None (Disable Humanize)" },
  ];
  return (
    <Card>
      <h2 className="text-text font-semibold mb-1">Humanize Model</h2>
      <SettingsSelect
        label="Model"
        value={currentValue}
        options={options}
        onChange={(v) => onSet(v)}
      />
      <p className="text-text-muted text-sm mt-3">
        Rewrites content to bypass AI detection. Pick a model above for all queues. Mirrored in <span className="text-text">Content Engine</span> on the Build Queue tab — change it in either place.
      </p>
      <p className="text-text-subtle text-xs mt-2">
        Note: If you use Winston or GPTZero as your AI detector, StealthGPT is recommended — the other models typically won't produce strong enough rewrites to pass those stricter detectors. For ZeroGPT, Gemini or OpenAI produce comparable results at a fraction of StealthGPT's cost.
      </p>
      <p className="text-text-subtle text-xs mt-2">
        Avoid picking Claude Sonnet if your content is generated with Claude (the default), since humanizing Claude output with Claude itself doesn't meaningfully change the AI signal.
      </p>
    </Card>
  );
}

// =========================================================================
// API Keys
// =========================================================================
const API_KEY_DEFINITIONS: {
  key: SettingKey;
  label: string;
  notes?: string;
  testable: boolean;
  warning?: string;
  link?: { href: string; text: string };
}[] = [
  { key: "anthropic_api_key", label: "Anthropic (Claude)", testable: true },
  { key: "openai_api_key", label: "OpenAI (DALL-E)", testable: true },
  { key: "dataforseo_login", label: "DataForSEO Login", testable: true },
  { key: "dataforseo_password", label: "DataForSEO Password", testable: false },
  { key: "pictory_client_id", label: "Pictory Client ID", testable: false },
  { key: "pictory_client_secret", label: "Pictory Client Secret", testable: false },
  {
    key: "winston_ai_api_key",
    label: "Winston AI (AI Detection)",
    testable: true,
    warning:
      "Winston AI charges per scan for AI detection scoring. Each article generated will consume detection credits. This will add significant cost to your content production. Monitor your balance to avoid generation failures.",
  },
  {
    key: "zerogpt_api_key",
    label: "ZeroGPT (AI Detection)",
    testable: true,
    notes:
      "Alternative to Winston AI. Generally cheaper but produces stricter scores; useful when you want a second opinion or just need an AI-content gate without needing high accuracy.",
    link: { href: "https://www.zerogpt.com/pricing", text: "ZeroGPT pricing" },
  },
  {
    key: "gptzero_api_key",
    label: "GPTZero (AI Detection)",
    testable: true,
    notes:
      "Alternative to Winston AI. Stricter than Winston/ZeroGPT — better for clients who specifically audit with GPTZero. Get an API key from app.gptzero.me/app/api.",
    link: { href: "https://app.gptzero.me/app/api", text: "Get GPTZero key" },
  },
  { key: "google_gemini_api_key", label: "Google Gemini (Image Gen)", testable: true },
  {
    key: "stealthgpt_api_key",
    label: "StealthGPT (Humanizer)",
    testable: true,
    warning:
      "StealthGPT charges ~$0.20 per 1,000 words (~$0.40-0.50 per article). A 500K word starter pack costs $100. This will add significant cost to your content production. Monitor your usage at stealthgpt.ai/dashboard.",
  },
  {
    key: "google_places_api_key",
    label: "Google Places API (New)",
    testable: true,
    notes:
      "Get a key at console.cloud.google.com → APIs & Services → Credentials → Create Credentials → API Key. Then enable 'Places API (New)' under APIs & Services → Library. The legacy 'Places API' will not work.",
    link: { href: "https://console.cloud.google.com", text: "Open Console" },
  },
];

function ApiKeysCard({
  settings,
  onSet,
  onTest,
}: {
  settings: SettingStatus[];
  onSet: (key: SettingKey, value: string | null) => Promise<void>;
  onTest: (key: SettingKey) => Promise<void>;
}) {
  return (
    <Card>
      <div className="flex items-start justify-between mb-3">
        <h2 className="text-text font-semibold">API Keys</h2>
        <Legend />
      </div>
      <div className="space-y-3">
        {API_KEY_DEFINITIONS.map((def) => {
          const status = settings.find((s) => s.key === def.key);
          return (
            <ApiKeyRow
              key={def.key}
              definition={def}
              status={status}
              onSet={onSet}
              onTest={onTest}
            />
          );
        })}
      </div>
    </Card>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-3 text-xs text-text-muted">
      <span className="flex items-center gap-1">
        <Dot kind="valid" /> Valid
      </span>
      <span className="flex items-center gap-1">
        <Dot kind="low_credits" /> Low credits
      </span>
      <span className="flex items-center gap-1">
        <Dot kind="invalid" /> Invalid
      </span>
      <span className="flex items-center gap-1">
        <Dot kind="none" /> Not set
      </span>
    </div>
  );
}

type DotKind = "valid" | "low_credits" | "invalid" | "none";
function Dot({ kind }: { kind: DotKind }) {
  const color = {
    valid: "bg-success",
    low_credits: "bg-warning",
    invalid: "bg-danger",
    none: "bg-text-subtle",
  }[kind];
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
}

function statusToDot(status: SettingStatus | undefined): DotKind {
  if (!status || !status.isSet) return "none";
  if (!status.validationStatus) return "valid"; // Set but never tested
  return status.validationStatus;
}

function ApiKeyRow({
  definition,
  status,
  onSet,
  onTest,
}: {
  definition: (typeof API_KEY_DEFINITIONS)[number];
  status: SettingStatus | undefined;
  onSet: (key: SettingKey, value: string | null) => Promise<void>;
  onTest: (key: SettingKey) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const isSet = status?.isSet ?? false;
  const sourceLabel =
    status?.source === "env" ? "(env)" : status?.source === "ui" ? "Set" : "Not set";
  const dotKind = statusToDot(status);

  async function handleSave() {
    setBusy(true);
    try {
      await onSet(definition.key, draft);
      // Only clear the input on success. If onSet throws (parent already
      // alerted the user), keep the typed value visible so they can retry.
      setEditing(false);
      setDraft("");
    } catch {
      // Parent already surfaced the error; nothing else to do here.
    } finally {
      setBusy(false);
    }
  }

  async function handleTest() {
    setBusy(true);
    try {
      await onTest(definition.key);
    } finally {
      setBusy(false);
    }
  }

  async function handleClear() {
    if (!confirm(`Clear ${definition.label}?`)) return;
    setBusy(true);
    try {
      await onSet(definition.key, null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-border-subtle rounded-md p-3 bg-bg-subtle/30">
      <div className="flex items-center gap-2 mb-1">
        <Dot kind={dotKind} />
        <span className="text-text font-medium text-sm">{definition.label}</span>
        <span className="text-text-subtle text-xs">{sourceLabel}</span>
        {status?.validatedAt && (
          <span
            className="text-text-subtle text-xs ml-auto"
            title={status.validationMessage ?? ""}
          >
            Tested {new Date(status.validatedAt).toLocaleDateString()}
          </span>
        )}
      </div>

      {definition.notes && (
        <p className="text-text-subtle text-xs mb-2">
          {definition.notes}
          {definition.link && (
            <>
              {" "}
              <a
                href={definition.link.href}
                target="_blank"
                rel="noreferrer"
                className="text-accent hover:underline"
              >
                {definition.link.text}
              </a>
            </>
          )}
        </p>
      )}

      <div className="flex items-center gap-2">
        <input
          type="password"
          value={editing ? draft : isSet ? "••••••••" : ""}
          onChange={(e) => {
            if (!editing) {
              setEditing(true);
              setDraft(e.target.value);
            } else {
              setDraft(e.target.value);
            }
          }}
          onFocus={() => {
            if (!editing && isSet) {
              setEditing(true);
              setDraft("");
            }
          }}
          placeholder={isSet ? "" : "Not set"}
          className="flex-1 bg-bg-card border border-border text-text rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
        />

        {editing ? (
          <>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={busy || draft.trim().length === 0}
            >
              Save
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setEditing(false);
                setDraft("");
              }}
              disabled={busy}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setEditing(true);
                setDraft("");
              }}
              disabled={busy}
            >
              {isSet ? "Update" : "Set"}
            </Button>
            {definition.testable && isSet && (
              <Button variant="secondary" size="sm" onClick={handleTest} disabled={busy}>
                {busy ? "..." : "Test"}
              </Button>
            )}
            {isSet && status?.source === "ui" && (
              <Button variant="secondary" size="sm" onClick={handleClear} disabled={busy}>
                Clear
              </Button>
            )}
          </>
        )}
      </div>

      {definition.warning && (
        <div className="mt-2 bg-warning-bg border border-warning-border rounded-md px-3 py-2">
          <p className="text-warning text-xs">⚠ {definition.warning}</p>
        </div>
      )}
      {status?.validationMessage && status.validationStatus === "invalid" && (
        <p className="text-danger text-xs mt-1.5">{status.validationMessage}</p>
      )}
      {status?.validationMessage && status.validationStatus === "low_credits" && (
        <div className="mt-1.5 flex items-center gap-2">
          <p className="text-warning text-xs">{status.validationMessage}</p>
          <button
            type="button"
            onClick={handleTest}
            disabled={busy}
            className="text-accent text-xs hover:underline disabled:opacity-50"
            title="Re-check this provider's balance now"
          >
            {busy ? "..." : "↻ Refresh"}
          </button>
        </div>
      )}
      {/* fix34: show the balance even when status is "valid" — for providers
          that report a balance number (DataForSEO, Winston, StealthGPT) the
          message will say e.g. "Winston AI key is valid. 388,703 credits remaining."
          We surface that here in a muted color (not warning) so the user can
          glance at Settings and see actual balances without clicking Test.
          For "valid" providers that don't report numbers (Anthropic, Gemini,
          Places) the message is just "X is valid." — same treatment, harmless. */}
      {status?.validationMessage &&
        status.validationStatus === "valid" &&
        /(remaining|balance|credits)/i.test(status.validationMessage) && (
          <div className="mt-1.5 flex items-center gap-2">
            <p className="text-text-subtle text-xs">{status.validationMessage}</p>
            <button
              type="button"
              onClick={handleTest}
              disabled={busy}
              className="text-accent text-xs hover:underline disabled:opacity-50"
              title="Re-check this provider's balance now"
            >
              {busy ? "..." : "↻ Refresh"}
            </button>
          </div>
        )}
    </div>
  );
}

// =========================================================================
// YouTube / Google OAuth
// =========================================================================
function YouTubeOAuthCard({
  clientId,
  clientSecret,
  onSet,
}: {
  clientId: SettingStatus | undefined;
  clientSecret: SettingStatus | undefined;
  onSet: (key: SettingKey, value: string | null) => Promise<void>;
}) {
  return (
    <Card>
      <h2 className="text-text font-semibold mb-1">YouTube / Google OAuth</h2>
      <p className="text-text-muted text-sm mb-3">
        Required for uploading generated videos to YouTube. Both fields come from the same OAuth 2.0 Client ID in Google Cloud Console.
      </p>
      <div className="space-y-3">
        <SimpleSecretRow
          label="Google Client ID"
          status={clientId}
          settingKey="google_oauth_client_id"
          onSet={onSet}
          link={{ href: "https://console.cloud.google.com", text: "Open Console" }}
          notes="Required for YouTube upload. Get from Google Cloud Console → APIs & Services → Credentials → OAuth 2.0 Client IDs."
        />
        <SimpleSecretRow
          label="Google Client Secret"
          status={clientSecret}
          settingKey="google_oauth_client_secret"
          onSet={onSet}
          notes="Pair with the Client ID above."
        />
      </div>
    </Card>
  );
}

function SimpleSecretRow({
  label,
  status,
  settingKey,
  onSet,
  link,
  notes,
}: {
  label: string;
  status: SettingStatus | undefined;
  settingKey: SettingKey;
  onSet: (key: SettingKey, value: string | null) => Promise<void>;
  link?: { href: string; text: string };
  notes?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const isSet = status?.isSet ?? false;
  const sourceLabel =
    status?.source === "env" ? "(env)" : status?.source === "ui" ? "Set" : "Not set";

  async function save() {
    await onSet(settingKey, draft);
    setEditing(false);
    setDraft("");
  }

  return (
    <div className="border border-border-subtle rounded-md p-3 bg-bg-subtle/30">
      <div className="flex items-center gap-2 mb-1">
        <Dot kind={statusToDot(status)} />
        <span className="text-text font-medium text-sm">{label}</span>
        <span className="text-text-subtle text-xs">{sourceLabel}</span>
      </div>
      {notes && (
        <p className="text-text-subtle text-xs mb-2">
          {notes}
          {link && (
            <>
              {" "}
              <a
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="text-accent hover:underline"
              >
                {link.text}
              </a>
            </>
          )}
        </p>
      )}
      <div className="flex items-center gap-2">
        <input
          type="password"
          value={editing ? draft : isSet ? "••••••••" : ""}
          onChange={(e) => {
            if (!editing) setEditing(true);
            setDraft(e.target.value);
          }}
          onFocus={() => {
            if (!editing && isSet) {
              setEditing(true);
              setDraft("");
            }
          }}
          placeholder="Not set"
          className="flex-1 bg-bg-card border border-border text-text rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
        />
        {editing ? (
          <>
            <Button variant="primary" size="sm" onClick={save} disabled={!draft.trim()}>
              Save
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setEditing(false);
                setDraft("");
              }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            {isSet ? "Update" : "Set"}
          </Button>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Reusable bits
// =========================================================================
function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
      {children}
    </div>
  );
}

function SettingsSelect({
  label,
  value,
  options,
  onChange,
  readOnly,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void | Promise<void>;
  /**
   * fix39: when true, the select is disabled and styled as a read-only
   * mirror. Used by the three provider/model cards in Settings — they now
   * just show what's saved server-side, while the active editing happens
   * in Content Engine on the Build Queue.
   */
  readOnly?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm text-text font-medium mb-1.5">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={readOnly}
        className={`w-full bg-bg-card border border-border text-text rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent ${
          readOnly ? "opacity-70 cursor-not-allowed" : ""
        }`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
